/**
 * Reproductor de la jugada del gol.
 *
 * Dibuja posiciones grabadas, no vuelve a simular nada: lo que se ve es
 * exactamente lo que paso. Ver tu propio gol otra vez es lo que convierte un
 * marcador en una historia que contar.
 */
import "./clipPlayer.css";
import { t } from "../i18n/index.js";
import { FIELD } from "@golazo/shared";
import { clipDuration, clipFrameAt, type Clip } from "../game/clipRecorder.js";

const PLAYER_R = 22;
const BALL_R = 11;
/** Segundos de pausa al acabar antes de repetir. */
const LOOP_PAUSE = 0.9;

export interface ClipPlayerHandle {
  readonly canvas: HTMLCanvasElement;
  play(): void;
  stop(): void;
  dispose(): void;
}

function reducedMotion(): boolean {
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function createClipPlayer(container: HTMLElement, clip: Clip): ClipPlayerHandle {
  container.classList.add("clip");
  container.innerHTML = `
    <canvas class="clip-canvas" data-clip-canvas></canvas>
    <div class="clip-bar"><span data-clip-progress></span></div>
    <button type="button" class="clip-replay" data-clip-replay aria-label="${t("Repetir")}">↻ ${t("Repetir")}</button>
  `;
  const canvas = container.querySelector("[data-clip-canvas]") as HTMLCanvasElement;
  const progress = container.querySelector("[data-clip-progress]") as HTMLElement;
  const replayBtn = container.querySelector("[data-clip-replay]") as HTMLButtonElement;
  const ctx = canvas.getContext("2d");

  const duration = clipDuration(clip);
  const scorerIndex = clip.players.findIndex(
    (p) => clip.scorer !== null && p.shirtNumber === clip.scorer.shirtNumber && p.side === clip.side,
  );

  let running = false;
  let raf = 0;
  let startedAt = 0;
  let holdUntil = 0;

  const draw = (t: number): void => {
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssW = canvas.clientWidth || 1;
    const cssH = canvas.clientHeight || 1;
    if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
    }
    const scale = Math.min(cssW / FIELD.WIDTH, cssH / FIELD.HEIGHT);
    const offX = (cssW - FIELD.WIDTH * scale) / 2;
    const offY = (cssH - FIELD.HEIGHT * scale) / 2;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#0b1f13";
    ctx.fillRect(0, 0, cssW, cssH);
    ctx.save();
    ctx.translate(offX, offY);
    ctx.scale(scale, scale);

    ctx.fillStyle = "#123420";
    ctx.fillRect(0, 0, FIELD.WIDTH, FIELD.HEIGHT);
    ctx.fillStyle = "rgba(255,255,255,0.022)";
    for (let x = 0; x < FIELD.WIDTH; x += 160) ctx.fillRect(x, 0, 80, FIELD.HEIGHT);

    ctx.strokeStyle = "rgba(255,255,255,0.45)";
    ctx.lineWidth = 5;
    ctx.strokeRect(0, 0, FIELD.WIDTH, FIELD.HEIGHT);
    ctx.beginPath();
    ctx.moveTo(FIELD.WIDTH / 2, 0);
    ctx.lineTo(FIELD.WIDTH / 2, FIELD.HEIGHT);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(FIELD.WIDTH / 2, FIELD.HEIGHT / 2, 130, 0, Math.PI * 2);
    ctx.stroke();
    for (const side of [0, 1]) {
      const x = side === 0 ? 0 : FIELD.WIDTH - FIELD.BOX_WIDTH;
      ctx.strokeRect(x, (FIELD.HEIGHT - FIELD.BOX_HEIGHT) / 2, FIELD.BOX_WIDTH, FIELD.BOX_HEIGHT);
    }

    // Interpolamos entre muestras: a 30 Hz, sin esto se ve a saltos.
    const exact = Math.min(t / duration, 1) * (clip.frames - 1);
    const i0 = Math.floor(exact);
    const i1 = Math.min(clip.frames - 1, i0 + 1);
    const k = exact - i0;
    const a = clipFrameAt(clip, i0);
    const b = clipFrameAt(clip, i1);
    const lerp = (p: number, q: number): number => p + (q - p) * k;

    for (let i = 0; i < clip.players.length; i++) {
      const info = clip.players[i]!;
      const pa = a.players[i]!;
      const pb = b.players[i]!;
      const isScorer = i === scorerIndex;
      drawHex(
        ctx,
        lerp(pa.x, pb.x),
        lerp(pa.y, pb.y),
        PLAYER_R,
        info.side === "home" ? "#c0392b" : "#2c5fd6",
        isScorer ? "#ffe082" : "rgba(255,255,255,0.75)",
        isScorer ? 5 : 2.5,
      );
    }

    ctx.fillStyle = "#fff6d8";
    ctx.beginPath();
    ctx.arc(lerp(a.ball.x, b.ball.x), lerp(a.ball.y, b.ball.y), BALL_R, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
    progress.style.transform = `scaleX(${Math.min(1, t / duration)})`;
  };

  const frame = (now: number): void => {
    if (!running) return;
    if (startedAt === 0) startedAt = now;
    const t = (now - startedAt) / 1000;

    if (t >= duration) {
      // Pausa breve al final antes de repetir: el bucle seco marea.
      if (holdUntil === 0) holdUntil = now + LOOP_PAUSE * 1000;
      draw(duration);
      if (now >= holdUntil) {
        holdUntil = 0;
        startedAt = now;
      }
    } else {
      draw(t);
    }
    raf = requestAnimationFrame(frame);
  };

  const play = (): void => {
    startedAt = 0;
    holdUntil = 0;
    if (reducedMotion()) {
      // Sin animacion: se ensena el desenlace, que es lo que importa.
      draw(duration);
      return;
    }
    if (running) return;
    running = true;
    raf = requestAnimationFrame(frame);
  };

  replayBtn.addEventListener("click", () => {
    startedAt = 0;
    holdUntil = 0;
    if (!running) play();
  });

  const onVisibility = (): void => {
    // Volver de otra pestana no debe saltar medio clip de golpe.
    if (!document.hidden && running) startedAt = 0;
  };
  document.addEventListener("visibilitychange", onVisibility);

  return {
    canvas,
    play,
    stop() {
      running = false;
      cancelAnimationFrame(raf);
      raf = 0;
    },
    dispose() {
      this.stop();
      document.removeEventListener("visibilitychange", onVisibility);
      container.innerHTML = "";
    },
  };
}

function drawHex(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  fill: string,
  stroke: string,
  lineWidth: number,
): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 6;
    const x = cx + Math.cos(angle) * r;
    const y = cy + Math.sin(angle) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = stroke;
  ctx.stroke();
}
